import React, { useEffect, useState, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api, globalApiConfig } from 'careand-redux/api/ApiThunks';
import FormButton from 'components/Common/FormButton';
import { useIsLoading, useNavStack, useNavTab } from 'careand-redux/slices/ui/hooks';
import './style.scss';
import TopBar from 'scenes/HomePatient2/TopBar';
import { Stacks, Tabs } from 'careand-redux/slices/ui/navigation';
import { useQueryParams } from 'components/Navigation/NavStack';
import { store, useAppSelector } from 'careand-redux/store';
import { shouldShowPermissionScreen } from 'scenes/HomePatient2/Permissions';
import { authActions } from 'careand-redux/slices/auth';
import MiscUtils from 'services/MiscUtils';
import { Device } from 'services/Native/Device';
import { updatePushToken } from 'services/updatePushToken';
import config from 'config';
import io from 'socket.io-client';
import {
  AdminLoginPayload,
  Authentication,
  ManagerLoginPayload,
  Patient,
  PatientLoginPayload,
  PhysicianLoginPayload,
} from 'generated/api';
import EntryView from 'scenes/HomePatient2/PatientEntry/view';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';

import { loginSchema, LoginValues } from 'types/forms';
import {
  deepLinkActions,
  navigateToIntent,
  selectFreshIntent,
} from '../../careand-redux/slices/deep-link';
import { TenantAsset, TenantId } from '../../careand-redux/selectors';
import { logout } from '../../careand-redux/actions/logout';
import { getTenantIdentity } from 'hooks/useTenantIdentity';
import TenantImg from '../../components/TenantImg';

const AUTH_FAILED_TITLE = 'Authentication Failed';
const AUTH_FAILED_TEXT = 'Username or Password are incorrect';
const URL = config.API_URL;

interface AuthValues {
  username?: string;
  password?: string;
  code?: string;
}

let loginSocket: any;

const NewLogin = () => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const { link, manager } = useQueryParams() as { link?: string; manager?: string };
  const isLinking = link === 'true';
  const isLoggedIn = useAppSelector((state) => state.authentication.loggedIn);
  const savedUsername = useAppSelector((state) => state.authentication.username);
  const tenantId = useAppSelector(TenantId);

  const [code, setCode] = useState('');
  const [loginSession, setLoginSession] = useState();

  const { control, handleSubmit, reset, watch } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenant: undefined,
      username: savedUsername || '',
      password: '',
    },
  });
  const tenant = watch('tenant') ?? tenantId;

  const loading = useIsLoading(api.Authentication.userLogin);
  const { login, show2FA } = useLogin({ isLinking, loginSession });

  useEffect(() => {
    if (!isLinking && isLoggedIn) {
      nav.popTo('/');
    }
  }, [isLoggedIn, isLinking]);

  useEffect(() => {
    function setupLoginSocket() {
      loginSocket = io(URL, {
        transports: ['websocket'],
        query: `loginSession=true&tenant_id=${encodeURIComponent(tenant!)}`,
      });
      loginSocket.on('login-session', (data: any) => {
        setLoginSession(data.id);
      });
      loginSocket.on('login-token', (data: any) => {
        const { auth, role, username } = data;
        dispatch(authActions.setLoginPayload({ username, auth, role }));
      });
    }

    const removeLoginSocket = () => {
      if (loginSocket) loginSocket.close();
    };

    setupLoginSocket();
    return removeLoginSocket;
  }, [tenant]);

  useEffect(() => {
    reset();
  }, [reset, savedUsername]);

  const onLoginSubmit = async (values: LoginValues) => {
    const { tenant, ...authValues } = values;
    const payload: AuthValues = code ? { ...authValues, code } : authValues;

    await login(payload, tenant || tenantId!);
  };

  function onForgotUsername() {
    nav.push('/login/forgot-username');
  }

  function onForgotPassword() {
    nav.push('/login/forgot-password');
  }

  return (
    <EntryView className="login-patient" topContent={<TopBar title="Sign In" />}>
      <TenantImg alt={getTenantIdentity().name} src="logo_full_color" style={{ width: '240px' }} />
      <form className="login-form mt-4" onSubmit={handleSubmit(onLoginSubmit)}>
        {!tenantId && <DefaultPatientTextField name="tenant" control={control} label="Tenant" />}
        <DefaultPatientTextField
          name="username"
          control={control}
          label="Username"
          type="username"
        />
        <DefaultPatientTextField
          name="password"
          control={control}
          label="Password"
          type="password"
        />
        {show2FA && (
          <DefaultPatientTextField
            id="code-input"
            name="code"
            label="2FA Code"
            value={code}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        )}
        <FormButton id="login-button" name="Sign In" loading={loading} />
      </form>
      <div className="links">
        <span className="link" id="forgot-username" onClick={onForgotUsername}>
          Forgot username?
        </span>
        <span className="link" id="forgot-password" onClick={onForgotPassword}>
          Forgot password?
        </span>
      </div>
    </EntryView>
  );
};

/**
 * Handles the login process
 * returns a login function
 */
export function useLogin({
  isLinking,
  loginSession,
}: {
  isLinking?: boolean;
  loginSession?: string;
}) {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const { switchTab } = useNavTab();
  const devicePermissions = useAppSelector((state) => state.device.permissions);
  const [show2FA, setShow2FA] = useState(false);
  const clientRole = useAppSelector((state) => state.device.clientRole);

  async function linkPatientAccount(value: AuthValues) {
    let { username, password } = value;
    username = username?.trim();
    if (!username || !password) {
      return;
    }
    const result = await dispatch(
      api.Authentication.addLinkedAccount({ authentication: { username, password } }),
      {
        errorTitle: 'Authentication Failed',
      },
    );
    if (result?.payload?.user) {
      dispatch(authActions.changeActiveAccount(result.payload.user.id!));
      nav.popTo('/', { animation: 'push' });
      MiscUtils.showSuccess('Account Linked', 'Account linked successfully');
    }
  }

  async function newLogin(values: AuthValues, tenantId: string) {
    let { username, password, code } = values;
    username = username?.trim();
    if (!username || !password) {
      return;
    }

    let parsedCode = '' + code;
    let auth: Authentication = {
      username,
      password,
      code: parsedCode.length === 6 ? parsedCode : undefined,
      login_session: loginSession ? loginSession : undefined,
    };

    if (!code && Device.isMobile()) {
      auth.code = (await Device.requestOTPCode({ username }))?.code;
    }

    const result = await dispatch(
      api.Authentication.userLogin({
        authentication: auth,
        $$extraHeaders: {
          tenant_id: tenantId,
        },
      }),
    );

    if (result) {
      const role = result.role;
      const payload = result[role]!;

      // if a native device claims to have a device role we reject logins of a different role
      if (clientRole) {
        const matchesPatient = clientRole === 'patient' && role === 'patient';
        const matchesTeam = clientRole === 'team' && role !== 'patient';
        if (!matchesPatient && !matchesTeam) {
          dispatch(logout());
          return;
        }
      }

      if (payload.token) {
        if (Device.isMobile()) {
          // Merge over the existing record (overwrite: true replaces the whole
          // stored user natively) so a changed password is refreshed without
          // wiping fields only the 2FA flows write, like the otp secret
          const existing = await Device.requestStoredUserCredentials(payload.user!.id!);
          await Device.requestToStoreUser(
            {
              ...existing,
              username,
              password,
              userType: role,
              userId: payload.user!.id!,
              tenantId: tenantId ?? existing?.tenantId,
            },
            true,
          );
        }
        onLoginSuccess({ username, password }, payload, role);
      } else {
        setShow2FA(true);
      }
    } else {
      MiscUtils.showToastrError(AUTH_FAILED_TITLE, AUTH_FAILED_TEXT);
    }
  }

  function onLoginSuccess(
    credentials: { username: string; password: string },
    result: PatientLoginPayload | AdminLoginPayload | PhysicianLoginPayload | ManagerLoginPayload,
    userType: 'physician' | 'admin' | 'patient' | 'manager',
  ) {
    if (userType === 'patient') {
      if (Device.isMobile()) {
        Device.requestToStoreUser(
          {
            ...credentials,
            userType,
            userId: result.user!.id!,
            tenantId: (result.user as Patient).tenant_id!,
          },
          true,
        );
        Device.requestSetActiveUserId(result.user!.id!);
        //TODO get rid of old way of doing this
        Device.storeCredentials(credentials);
      }
      updatePushToken(result.user?.id);
      switchTab(Tabs.Home);
      if (shouldShowPermissionScreen(devicePermissions)) {
        nav.pushAsFirst('/permissions');
      } else {
        nav.popTo('/', { animation: 'push' });
      }
    } else {
      nav.pushAsFirst('/');
      if (Device.isMobile()) {
        Device.requestSetActiveUserId(result.user!.id!);
      }
    }
    const intent = selectFreshIntent(store.getState(), userType);

    if (intent) {
      setTimeout(() => {
        dispatch(deepLinkActions.clearIntent());
        dispatch(navigateToIntent(intent));
      }, 1);
    }
  }
  return { login: isLinking ? linkPatientAccount : newLogin, show2FA };
}

export default NewLogin;
